import { all, put, call, getContext, takeLatest } from 'redux-saga/effects';

import { Logger } from 'utils/Logger';

import {
  activeLenders,
  clientsAccounts,
  facilitiesMap,
  deleteFacility,
  saveFacilityType,
  businessFundingSearches,
  creditLinesFacilityMap,
  SSE_UPDATE_FACILITY_GENERATION_PROGRESS,
} from './types';

import {
  requestActiveLendersSuccess,
  requestClientAccountsSuccess,
  requestLendersSuccess,
  requestFacilitiesMapSuccess,
  saveFacilitySuccess,
} from './reducer';

import { requestFacilitiesMap as requestFacilitiesAction } from './actions';

const logger = new Logger('Saga:Monitor:Wizzard');

export function* requestClientAccounts(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.monitor.monitor_snapshot_accounts', {
        entityId: action.entityId,
      }),
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestClientAccountsSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request clients available accounts with the following error', error);

    action.promise && action.promise.reject(error);
  }
}

export function* requestActiveLenders() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(authApiService.get, routerService.generate('api.active_lenders'));

    yield put(requestActiveLendersSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request active lenders with the following error', error);
  }
}

export function* requestBusinessFundingSearches(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.monitor.business_funding_searches', {
        entityId: action.entityId,
      }),
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestLendersSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request clients lenders with the following error', error);
    action.promise && action.promise.reject(error);
  }
}

export function* postCreditLinesFacilityMap(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.monitor.entity_facilities', { entityId: action.entityId }),
      action.payload,
    );

    action.promise && action.promise.resolve(response.data);
  } catch (error) {
    logger.error('Failed to post credit lines map with the following error', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestFacilitiesMap(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.monitor.entity_facilities', { entityId: action.entityId }),
      action.payload,
    );

    action.promise && action.promise.resolve(response.data);

    yield put(requestFacilitiesMapSuccess(response.data));
  } catch (error) {
    logger.error('Failed to get facilities map with the following error', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestSaveFacility(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      action.isNewFacility ? authApiService.post : authApiService.put,
      routerService.generate('api.monitor.entity_facilities', { entityId: action.entityId }),
      [action.payload],
    );

    if (!action.isNewFacility) {
      yield put(saveFacilitySuccess(action.payload));
    }

    action.promise && action.promise.resolve(response.data);
  } catch (error) {
    logger.error('Failed to save facility with the following error', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestDeleteFacility(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.delete,
      routerService.generate('api.monitor.delete_facility', {
        entityId: action.entityId,
        id: action.id,
      }),
      [action.payload],
    );

    action.promise && action.promise.resolve(response.data);
  } catch (error) {
    logger.error('Failed to delete facility with the following error', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestFacilitiesMapUpdateSaga(action) {
  const entityId = action?.payload?.entityId;
  const message = action?.payload?.message;

  if (message === 'Facility generated') {
    yield put(requestFacilitiesAction(entityId));
  }
}

export default function* wizzardSaga() {
  return yield all([
    takeLatest(activeLenders.request, requestActiveLenders),
    takeLatest(clientsAccounts.request, requestClientAccounts),
    takeLatest(facilitiesMap.request, requestFacilitiesMap),
    takeLatest(deleteFacility.request, requestDeleteFacility),
    takeLatest(businessFundingSearches.request, requestBusinessFundingSearches),
    takeLatest(creditLinesFacilityMap.request, postCreditLinesFacilityMap),
    takeLatest(saveFacilityType.request, requestSaveFacility),
    takeLatest(SSE_UPDATE_FACILITY_GENERATION_PROGRESS, requestFacilitiesMapUpdateSaga),
  ]);
}
